import DateHelper from 'Utility/DateHelper';
import { PlayerTask } from './PlayerTasks';
import { getRewardData, IRewardData, isRewardUnlocked } from 'Utility/RewardHelper';
import { RewardType } from 'Utility/Rewards/RewardType';
import { IAudioData, IFriendGiftRewardActionPayload, IShowLevelAutoFlowPopupParams } from './PlayerActions';
import { TimerType } from 'System/Timer/TimerTypes';
import { FTUXType } from 'Player/PlayerProperty/Progress/FTUXType';
import { shopGiftReward } from 'Constants/Economy';
import { FBTrackingEvent } from 'Utility/Tracking/FBTrackingEvent';
import { HeavyUsersTracking } from 'Utility/Tracking/TrackingHelpers/HeavyUsersTracking';
import { WorldDataHelper } from 'Utility/WorldDataHelper';
import { isDynamicAILevel } from '../Level/LevelStateHelper';
import { LevelHelperSettings } from 'Constants/Level';
import UIStateHelper from '../UI/UIStateHelper';
import { getCurrentLevelNumber, getHighestLevelCompleted } from './PlayerLevelHelper';
import { IGameState } from '../Game/IGameState';
import { IPlayerState, IPlayerStateTaskData } from './IPlayerState';
import { StarChestHelper } from 'Utility/StarChestHelper';
import { Quests } from './QuestType';
import { TActionType, TSocialActionType, TransactionType } from 'Utility/Tracking/DataSet/Transaction';
import DailyLoginHelper from 'Utility/DailyLoginHelper';
import { ConditionalTrackingHelper } from 'Utility/Tracking/TrackingHelpers/ConditionalTracking';
import { LoginStateHelper } from './Login/LoginStateHelper';
import { QuestStateHelper } from './Quests/QuestStateHelper';
import { StarChestStateHelper } from './StarChests/StarChestStateHelper';
import { ProgressStateHelper } from './Progress/ProgressStateHelper';
import { InventoryStateHelper } from './Inventory/InventoryStateHelper';
import { SocialStateHelper } from './Social/SocialStateHelper';
import TimerStateHelper from '../Timer/TimerStateHelper';
import { FTUXStateHelper } from '../Game/FTUX/FTUXStateHelper';
import { PopupID } from '../UI/PopupID';
import { LuckyWheelStateHelper } from './LuckyWheel/LuckyWheelStateHelper';
import { AdjustLevelEventsMap } from 'Utility/Tracking/AdjustTracking';
import Game from 'Game';
import { report } from 'report';
import { FarmStateHelper } from './Farm/FarmStateHelper';
import { OnShowProps } from 'State/UI/UIActions';
import { PetsStateHelper } from 'State/Player/Pets/PetsStateHelper';
import { LevelType } from './Progress/PlayerLevels';
import { Random } from 'Utility/Random';
import { track } from 'Utility/Tracking/Track';
import { trackResourceEvent } from 'Utility/Tracking/TrackingHelpers/Transaction';
import { NonVariableRewards } from 'Types/Reward';
import { SeasonPassStateHelper } from './SeasonPass/SeasonPassStateHelper';

export const SUNDAY_OFFER_DEBUG: boolean = false;

export namespace PlayerStateHelper {
    export const setDifficultyReduction = (oldState: IGameState, difficultyReduction: number): IGameState => {
        const state = TimerStateHelper.setTimer(oldState, TimerType.DYNAMIC_HELPER_AI, DateHelper.now);
        return {
            ...state,
            playerState: {
                ...state.playerState,
                progress: {
                    ...state.playerState.progress,
                    difficultyReduction,
                },
            },
        };
    };

    /**
     * Shows either pre-level popup, star chest info or more levels coming soon popup
     */
    export const showLevelAutoFlowPopup = (
        state: IGameState,
        params: IShowLevelAutoFlowPopupParams = {}
    ): IGameState => {
        const { allowRandomizedLevelSelection = true } = params;
        const levelNumber = params.levelNumber ?? getCurrentLevelNumber(state.playerState);
        const maxWorldLevel = WorldDataHelper.maxLevel(state.worldState.worldNumber);
        const highestLevelCompleted = getHighestLevelCompleted(state.playerState);
        const luckyWheelLevels = LuckyWheelStateHelper.getEligibleWheelLevels(
            state.playerState.lastClaimedSpinLevel,
            maxWorldLevel,
            highestLevelCompleted
        );

        // If the player has completed the `PET_UNLOCK` FTUX but not the current pet minigame queue a menu which simply dispatches a
        // `loadMyPetMiniGame` action. It is presumably triggered this way to conform to the standard auto-flow sequence of menus.
        // We do not queue this menu if the FTUX is not complete. This is because the FTUX also tries to dispatch a `loadMyPetMiniGame` action.
        // Unfortunately the `SceneManager` cannot handle multiple state changes during a scene load and causes errors (this should be fixed but
        // has long-reaching effects).
        if (
            levelNumber === state.playerState.petLevel &&
            FTUXStateHelper.isFtuxCompleted(state.playerState, FTUXType.PET_UNLOCK)
        ) {
            return showPopup(PopupID.PRE_PET_MINIGAME_MENU, {});
        }

        const dailyLoginTask = state.playerState.tasks.find(task => task.popup === PopupID.DAILY_LOGIN_MENU);
        if (
            LoginStateHelper.isDailyLoginUnlocked(state) && // If unlock requirement is met
            !FTUXStateHelper.isFtuxCompleted(state.playerState, FTUXType.DAILY_LOGIN) && // If daily login ftux has not been completed
            !dailyLoginTask && // If there isn't a daily login task already
            DailyLoginHelper.canCollectDailyLoginReward(state.playerState) // If reward can be collected (not sure if this is needed)
        ) {
            state = showPopup(PopupID.DAILY_LOGIN_MENU, {});
        }

        if (PlayerStateHelper.shouldOpenChest(state.playerState, levelNumber)) {
            return showPopup(PopupID.STAR_CHEST_INFO, { levelNumber });
            // If there aren't any quests to collect then a level related popup can be shown
        }

        const hasPremiumSpins = InventoryStateHelper.hasInInventory(state, RewardType.PREMIUM_SPIN);
        if (
            // Ftux is completed
            FTUXStateHelper.isFtuxCompleted(state.playerState, FTUXType.LUCKY_WHEEL) &&
            // If player has premium spins or if levelNumber is a spin wheel level
            (hasPremiumSpins ||
                (luckyWheelLevels.includes(levelNumber) && levelNumber > state.playerState.lastClaimedSpinLevel))
        ) {
            return showPopup(PopupID.WHEEL, { premium: hasPremiumSpins });
        }

        if (
            allowRandomizedLevelSelection &&
            !params.forceSkipLevelPopup &&
            levelNumber === WorldDataHelper.maxLevel() &&
            ProgressStateHelper.getLevelStars(state, levelNumber) > 0
        ) {
            const incompleteLevels = state.playerState.levels[LevelType.Core].filter(levelData => levelData.stars < 3);

            const levelPool = incompleteLevels.length > 0 ? incompleteLevels : state.playerState.levels[LevelType.Core];

            const randomLevelNumber = Random.getRandomItem(levelPool).levelNumber;
            return showPopup(PopupID.LEVEL_POPUP, { levelNumber: randomLevelNumber, fail: false });
        }

        if (!params.forceSkipLevelPopup) {
            return showPopup(PopupID.LEVEL_POPUP, { levelNumber, fail: false });
        }

        return state;

        function showPopup<T extends PopupID>(popupID: T, popupParams: OnShowProps<T>) {
            const alreadyQueued = state.playerState.tasks.some(
                task => task.task === PlayerTask.SHOW_POPUP && task.popup === popupID
            );
            if (alreadyQueued) return state;

            if (params.delay) {
                return {
                    ...state,
                    playerState: PlayerStateHelper.addStateTaskDatas(state.playerState, [
                        {
                            task: PlayerTask.SHOW_POPUP,
                            popup: popupID,
                            meta: popupParams,
                            delay: params.delay,
                        },
                    ]),
                };
            }
            return {
                ...state,
                UIState: UIStateHelper.showPopup(state.UIState, popupID, popupParams),
            };
        }
    };

    export const setPlayerState = (state: IGameState, playerState: IPlayerState): IGameState => {
        return {
            ...state,
            playerState: playerState,
        };
    };

    export const checkTimers = (state: IGameState): IGameState => {
        const levelHelperDate: Date = state.playerState.timers.get(TimerType.DYNAMIC_HELPER_AI);

        // Clears dynamic match chance on login if past cooldown time
        if (levelHelperDate && DateHelper.timeSince(levelHelperDate) >= LevelHelperSettings.idleTimeToResetDifficulty) {
            state = PlayerStateHelper.setDifficultyReduction(state, 0);
        }

        return state;
    };

    export const clearTasks = (state: IGameState, tasksToClear?: IPlayerStateTaskData[]): IGameState => {
        return {
            ...state,
            playerState: {
                ...state.playerState,
                tasks: tasksToClear ? state.playerState.tasks.filter(task => !tasksToClear.includes(task)) : [],
            },
        };
    };

    export const addStateTaskDatas = (
        state: IPlayerState,
        taskDatas: Array<IPlayerStateTaskData>,
        queueNext?: boolean
    ): IPlayerState => {
        let tasks = queueNext ? [...taskDatas, ...state.tasks] : [...state.tasks, ...taskDatas];

        // Keeps tasks like next level popup at the end
        tasks = [...tasks.filter(task => !isClosingTask(task)), ...tasks.filter(task => isClosingTask(task))];

        return {
            ...state,
            tasks,
        };
    };

    export const isClosingTask = (taskData: IPlayerStateTaskData): boolean => {
        return (
            taskData.task === PlayerTask.SHOW_POPUP &&
            (taskData.popup === PopupID.LEVEL_POPUP ||
                taskData.popup === PopupID.MORE_LEVELS_COMING ||
                taskData.popup === PopupID.STAR_CHEST_INFO ||
                taskData.popup === PopupID.CONTENT_END_LEVEL_POPUP)
        );
    };

    export const updatePlayerState = (oldState: IGameState, newPlayerState: IPlayerState): IPlayerState => {
        let state = {
            ...oldState,
            playerState: {
                ...oldState.playerState,
                ...newPlayerState,
            },
        };
        state = {
            ...state,
            playerState: {
                ...state.playerState,
                maxWorld: Math.max(1, state.playerState.maxWorld),
            },
        };
        //state = PlayerStateHelper.updateTimers(state);
        state = QuestStateHelper.fillQuestsIfNeeded(state);
        return state.playerState;
    };

    export const levelComplete = (state: IGameState): IGameState => {
        const levelNumber: number = state.levelState.levelNumber;
        const { coins, stars, starsGained, stackReward, rewards } = state.levelState.completeData;
        const levelData = ProgressStateHelper.getLevelData(state, levelNumber, state.levelState.levelType);
        const newLevelData = {
            levelNumber,
            stars: levelData.stars && levelData.stars > stars ? levelData.stars : stars,
            consecutiveUnsuccessfullAttempts: 0,
        };

        const previousStarCount = StarChestStateHelper.getStarChestStarCount(state.playerState);
        const starCountCap = StarChestHelper.getStarCountCap();
        if (state.playerState.starChestLevel !== null && starsGained > 0 && previousStarCount < starCountCap) {
            state = {
                ...state,
                playerState: PlayerStateHelper.addStateTaskDatas(state.playerState, [
                    {
                        task: PlayerTask.STAR_CHEST_STAR_REWARD,
                        amount: starsGained,
                        previousStarCount,
                        currentStarChestTier: StarChestStateHelper.getStarChestUnlockTier(state.playerState),
                        levelNumber: state.levelState.levelNumber,
                    },
                ]),
            };
        }

        if (starsGained > 0) {
            state = {
                ...state,
                playerState: QuestStateHelper.incrementQuestProgressBy(
                    state.playerState,
                    Quests.COLLECT_STARS,
                    starsGained
                ),
            };
        }

        if (isDynamicAILevel(state)) {
            state = PlayerStateHelper.setDifficultyReduction(
                state,
                levelData.consecutiveUnsuccessfullAttempts < LevelHelperSettings.maxAttemptsToResetDifficulty
                    ? 0
                    : state.playerState.progress.difficultyReduction
            );
        }

        state = ProgressStateHelper.updateLevel(state, levelNumber, newLevelData, state.levelState.levelType);

        const firstComplete = !levelData.stars;
        if (firstComplete) {
            HeavyUsersTracking.trackHeavyUserLevelFirstCompleteIfNeeded(state.playerState);
            state = {
                ...state,
                playerState: QuestStateHelper.incrementQuestProgressBy(
                    state.playerState,
                    Quests.COMPLETE_LEVELS_1ST_TIME,
                    1,
                    TActionType.LEVEL_END
                ),
            };
        }

        state = ConditionalTrackingHelper.trackConditionally(state, [
            FBTrackingEvent.LVL_20_3DAYS,
            FBTrackingEvent.LVL_40_5DAYS,
            FBTrackingEvent.LVL_60_7DAYS,
            FBTrackingEvent.LVL_10_1DAY,
            FBTrackingEvent.LVL_10_2DAYS,
            FBTrackingEvent.LVL_10_3DAYS,
            FBTrackingEvent.LVL_15_1DAY,
            FBTrackingEvent.LVL_15_2DAYS,
            FBTrackingEvent.LVL_15_3DAYS,
            FBTrackingEvent.LVL_20_1DAY,
            FBTrackingEvent.LVL_20_2DAYS,
            FBTrackingEvent.LVL_20_5DAYS,
            FBTrackingEvent.LVL_20_7DAYS,
        ]);

        if (coins > 0) {
            state = {
                ...state,
                playerState: InventoryStateHelper.setCoins(
                    state.playerState,
                    state.playerState.inventory.coins + coins + stackReward
                ),
            };
        }

        // Give on complete rewards
        if (rewards.length > 0) {
            state = {
                ...state,
                playerState: InventoryStateHelper.receiveRewards(state.playerState, rewards),
            };
        }

        if (
            levelNumber >= WorldDataHelper.maxLevel(state.playerState.maxWorld) &&
            state.playerState.maxWorld < WorldDataHelper.maxWorld()
        ) {
            state = {
                ...state,
                playerState: ProgressStateHelper.unlockNextWorld(state.playerState),
            };
        }

        state = QuestStateHelper.fillQuestsIfNeeded(state);

        const levelEvent = AdjustLevelEventsMap.get(state.levelState.levelNumber);
        if (levelEvent) {
            Game.sdk.gameTracking(levelEvent);
        }

        return state;
    };

    export const unlockAllPossibleWorlds = (state: IGameState) => {
        while (ProgressStateHelper.canUnlockNextWorld(state, getHighestLevelCompleted(state.playerState))) {
            state = {
                ...state,
                playerState: ProgressStateHelper.unlockNextWorld(state.playerState),
            };
        }
        return state;
    };

    export const spinWheel = (state: IPlayerState, isPremium: boolean): IPlayerState => {
        state = isPremium ? { ...state, spunFirstDiamondWheel: true } : { ...state, spunFirstNormalWheel: true };

        if (getCurrentLevelNumber(state) !== state.lastClaimedSpinLevel && !isPremium) {
            state = PlayerStateHelper.setLastClaimedSpinLevel(state);
        }

        return state;
    };

    export const subscribeBotOptIn = (state: IPlayerState): IPlayerState => {
        // Player got 500 coins in the first subscribe.
        if (state.progress.optInReward === 0) {
            state = {
                ...state,
                progress: {
                    ...state.progress,
                    optInReward: 1,
                },
            };
            state = InventoryStateHelper.setCoins(state, state.inventory.coins + 500);
        }

        return state;
    };

    export const completeWheelTimer = (oldState: IGameState): IGameState => {
        const state = TimerStateHelper.setTimer(oldState, TimerType.SPIN_WHEEL, null);
        return {
            ...state,
            playerState: {
                ...InventoryStateHelper.setWheelSpins(state.playerState, state.playerState.inventory.wheelSpins + 1),
            },
        };
    };

    export const queueFTUXIfNeeded = (state: IGameState): IGameState => {
        // TODO: Refactor queue methods to use full IGameState reference so it does not need to be exceptions e.g. FTUXStateHelper.queueEventFTUXIfNeeded()
        let { playerState } = state;

        playerState = StarChestStateHelper.queueStarChestFTUXIfNeeded(playerState);

        playerState = QuestStateHelper.queueQuestsFTUXIfNeeded(playerState);

        playerState = PetsStateHelper.queuePetFTUXIfNeeded(playerState);

        state = {
            ...state,
            playerState,
        };

        state = FarmStateHelper.queueFarmBtnFTUXIfNeeded(state);

        try {
            state = {
                ...state,
                playerState: LuckyWheelStateHelper.queueWheelFTUXIfNeeded(state.playerState),
            };
        } catch (err) {
            report('Failed to queue wheel FTUX', {
                sampleRate: 0.1,
                error: err,
            });
        }

        // Make sure to prepare this state using `playerState` from above.
        state = FTUXStateHelper.queueEventFTUXIfNeeded({
            ...state,
            playerState: state.playerState,
        });

        state = {
            ...state,
            playerState: SeasonPassStateHelper.queueSeasonPassFTUXIfNeeded(state.playerState)
        };

        return state;
    };

    export const updateTimers = (state: IPlayerState): IPlayerState => {
        //state = this.updateCurrencySharePrizeTimer(state);
        //state = this.updateGiftTimer(state);
        state = SocialStateHelper.updateFriendTimers(state);

        //TODO: Other timers.

        return state;
    };

    export const updateGiftTimer = (playerState: IPlayerState): IPlayerState => {
        // No timer set.
        if (playerState.timers.get(TimerType.GIFT) === null) {
            return playerState;
        }

        // Check if time is up.
        const now = DateHelper.now;
        const timeDiff = now.getTime() - playerState.timers.get(TimerType.GIFT).getTime();
        const twelveHours = DateHelper.hoursToMilliseconds(12);

        // One day has elapsed - remove the prize timer and reset all friends' gift cooldowns.
        if (timeDiff >= twelveHours) {
            const timers = new Map(playerState.timers);
            timers.set(TimerType.GIFT, null);
            return {
                ...playerState,
                timers,
                social: {
                    ...playerState.social,
                    friendData: playerState.social.friendData.map(friend => ({
                        ...friend,
                        giftCooldown: null,
                    })),
                },
            };
        }

        // Timer has not elapsed - no change.
        return playerState;
    };

    export const updateCurrencySharePrizeTimer = (playerState: IPlayerState): IPlayerState => {
        // No timer set.
        if (playerState.timers.get(TimerType.SEND_GIFT_REQUEST_DATE) === null) {
            return playerState;
        }

        // Check if time is up.
        const now = DateHelper.now;
        const timeDiff = now.getTime() - playerState.timers.get(TimerType.SEND_GIFT_REQUEST_DATE).getTime();
        const oneDay = DateHelper.daysToMilliseconds(1);

        // One day has elapsed - remove the prize timer.
        if (timeDiff >= oneDay) {
            const timers = new Map(playerState.timers);
            timers.set(TimerType.SEND_GIFT_REQUEST_DATE, null);
            return {
                ...playerState,
                timers,
            };
        }

        // No change.
        return playerState;
    };

    export const setAudio = (state: IGameState, audioData: IAudioData): IGameState => {
        state = { ...state };

        state.playerState.settings[audioData.type] = audioData.value;

        return state;
    };

    export const addFBTrackingEvent = (state: IGameState, event: FBTrackingEvent): IGameState => {
        return {
            ...state,
            playerState: {
                ...state.playerState,
                FBEvents: [...state.playerState.FBEvents, event],
            },
        };
    };

    export const claimShopGift = (oldState: IGameState): IGameState => {
        let state = {
            ...oldState,
            playerState: InventoryStateHelper.giveReward(oldState.playerState, shopGiftReward, { queueTask: true }),
        };
        state = {
            ...state,
            playerState: ProgressStateHelper.setShopGiftUses(
                state.playerState,
                state.playerState.progress.shopGiftsCollected + 1
            ),
        };

        state = TimerStateHelper.setTimer(state, TimerType.SHOP_GIFT, DateHelper.now);

        trackResourceEvent(state, { transactionType: TransactionType.REWARD, tActionType: TActionType.SHOP_GIFT });

        return {
            ...state,
            playerState: {
                ...InventoryStateHelper.setShopGift(state.playerState, state.playerState.inventory.shopGift - 1),
            },
        };
    };

    export const completeShopGiftTimer = (oldState: IGameState): IGameState => {
        const state = TimerStateHelper.setTimer(oldState, TimerType.SHOP_GIFT, null);

        return {
            ...state,
            playerState: {
                ...InventoryStateHelper.setShopGift(state.playerState, state.playerState.inventory.shopGift + 1),
            },
        };
    };

    export const completeAdCooldownTimer = (oldState: IGameState): IGameState => {
        // Reset ads watched
        const state = ProgressStateHelper.setAdsWatched(oldState, 0);
        // Reset timer
        return TimerStateHelper.setTimer(state, TimerType.AD_COOLDOWN, null);
    };

    // TODO: Move from social into playerState now that its used for more than just social features
    export const getGBCUserID = (state: IPlayerState): string => {
        return state.social.userId;
    };

    export const setGBCUserID = (state: IPlayerState, userId: string): IPlayerState => {
        return {
            ...state,
            social: {
                ...state.social,
                userId,
            },
        };
    };

    export const setIsNewUser = (state: IGameState): IGameState => {
        state = {
            ...state,
            perSessionData: {
                ...state.perSessionData,
                isNewUser: state.playerState.totalSessionCount <= 1,
            },
        };
        return state;
    };

    export const setLastClaimedSpinLevel = (state: IPlayerState): IPlayerState => {
        return {
            ...state,
            lastClaimedSpinLevel: getCurrentLevelNumber(state),
        };
    };

    export const shouldOpenChest = (_state: IPlayerState, _selectedLevel: number): boolean => {
        return (
            Boolean(_state.starChestLevel) &&
            getCurrentLevelNumber(_state) >= _state.starChestLevel &&
            _selectedLevel === _state.starChestLevel
        );
    };

    export const setDeviceId = (state: IGameState, deviceId: string): IGameState => {
        if (!deviceId) return state;
        return {
            ...state,
            playerState: {
                ...state.playerState,
                deviceId,
            },
        };
    };

    export const setFacebookUserId = (state: IGameState, facebookUserId: string): IGameState => {
        if (!facebookUserId) return state;
        return {
            ...state,
            playerState: {
                ...state.playerState,
                facebookUserId,
            },
        };
    };

    export const validatePlayerStateRestore = (playerState: IPlayerState): IPlayerState => {
        const highestLevelCompleted: number = getHighestLevelCompleted(playerState);
        playerState = validatePetLevel(playerState, highestLevelCompleted);
        playerState = validateStarChestLevel(playerState, highestLevelCompleted);
        return playerState;
    };

    export function validatePetLevel(playerState: IPlayerState, highestLevelCompleted: number): IPlayerState {
        if (
            PetsStateHelper.isAnyPetUnlocked(playerState.petsState) &&
            (!playerState.petLevel || playerState.petLevel < highestLevelCompleted)
        ) {
            playerState = PetsStateHelper.setPetLevel(playerState, highestLevelCompleted + 1);
            report('User already passed pet level', {
                once: true,
                sampleRate: 0.1,
                fingerprint: 'ValidatePetLevel:LevelTooLow',
            });
        }
        return playerState;
    }

    export function validateStarChestLevel(playerState: IPlayerState, highestLevelCompleted: number): IPlayerState {
        if (
            playerState.starChestLevel &&
            FTUXStateHelper.isFtuxCompleted(playerState, FTUXType.STAR_CHEST) &&
            playerState.starChestLevel < highestLevelCompleted
        ) {
            playerState = StarChestStateHelper.setStarChestLevel(playerState, null);
            report('User already passed star chest level', {
                once: true,
                sampleRate: 0.1,
                fingerprint: 'ValidateStarChestLevel:LevelTooLow',
            });
        }
        return playerState;
    }

    export function receiveCurrencyShareReward(state: IGameState, currencyShareReward: IRewardData): IGameState {
        state = {
            ...state,
            playerState: {
                ...InventoryStateHelper.giveReward(state.playerState, currencyShareReward, { queueTask: false }),
                progress: {
                    ...state.playerState.progress,
                    currencyShareCount: 0,
                },
            },
        };
        track('tCoinsReceived', currencyShareReward.value);
        track('socialActionType', TSocialActionType.GET_COINS);
        trackResourceEvent(state, {
            transactionType: TransactionType.REWARD,
            tActionType: TActionType.SOCIAL,
        });
        return state;
    }

    export function receiveFriendGiftReward(state: IGameState, friendGift: IFriendGiftRewardActionPayload): IGameState {
        let playerState = InventoryStateHelper.giveReward(state.playerState, friendGift.reward);
        playerState = SocialStateHelper.retireMessage(playerState, friendGift.messageData.messageId);
        playerState = SocialStateHelper.setFriendGiftTimer(playerState, friendGift.messageData.friendId);
        track('socialActionType', TSocialActionType.GET_COINS);
        track('socialRecieve', 1);
        trackResourceEvent(state, {
            transactionType: TransactionType.REWARD,
            tActionType: TActionType.SOCIAL,
        });
        return {
            ...state,
            playerState: playerState,
        };
    }
}

// Move out of state helpers
export const scaleCoinRewardWithLevelCost = (reward: IRewardData, levelCost: number): IRewardData => {
    if (reward.type === RewardType.COINS) {
        return { ...reward, value: Math.round(reward.value * levelCost), scaleWithLevelCost: false };
    } else {
        return reward;
    }
};

export function convertToAvailableRewards(state: IGameState, rewards: NonVariableRewards): NonVariableRewards {
    return rewards.map(reward => {
        switch (reward.type) {
            case RewardType.BULLSEYE:
            case RewardType.BOMB:
            case RewardType.SHUFFLE:
            case RewardType.PRE_BOOSTER_CANISTER:
            case RewardType.PRE_BOOSTER_JOKER:
            case RewardType.PRE_BOOSTER_REMOVE_THREE:
                if (!isRewardUnlocked(state.playerState, reward.type))
                    reward = {
                        type: RewardType.COINS,
                        amount: getRewardData(reward.type).fallbackCoins * reward.amount,
                    };
                break;
        }
        return reward;
    });
}
